import React, { useMemo, useEffect, useState, Suspense, useRef } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { OrbitControls, Preload } from '@react-three/drei'
import { Card } from './Card'
import { generateRandomPosition, getShuffledCards, CardWithPosition } from './utils'
import { BaseCard } from '@/lib/types/index'
import * as THREE from 'three'

// Floating dust particles — subtle depth cue
function DustParticles({ count = 200 }: { count?: number }) {
  const ref = useRef<THREE.Points>(null)
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3)
    for (let i = 0; i < count; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 100
      arr[i * 3 + 1] = (Math.random() - 0.5) * 60
      arr[i * 3 + 2] = (Math.random() - 0.5) * 80 - 20
    }
    return arr
  }, [count])

  useFrame((state) => {
    if (!ref.current) return
    ref.current.rotation.y = state.clock.getElapsedTime() * 0.005
    ref.current.rotation.x = Math.sin(state.clock.getElapsedTime() * 0.003) * 0.02
  })

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.08}
        color="#ffffff"
        transparent
        opacity={0.12}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  )
}

interface TarotCardBackgroundProps {
  baseDeck: BaseCard[];
}

export const TarotCardBackground: React.FC<TarotCardBackgroundProps> = ({ baseDeck }) => {
  const [isMobile, setIsMobile] = useState(false)
  const shuffledCards = useMemo(() => getShuffledCards(baseDeck), [baseDeck])

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const cardsWithPosition = useMemo(() => {
    const cardCount = isMobile ? 16 : 32
    return shuffledCards.slice(0, cardCount).map(card => ({
      ...card,
      position: generateRandomPosition(isMobile ? 30 : 40),
      speed: Math.random() * 0.012 + 0.02,
    })) as CardWithPosition[]
  }, [shuffledCards, isMobile])

  return (
    <div className="absolute inset-0 -z-10">
      <Canvas
        camera={{ position: [0, 0, isMobile ? 15 : 10], fov: 75 }}
        dpr={[1, 1.5]}
      >
        {/* Fog for depth */}
        <fog attach="fog" args={['#0a0a0a', 15, 60]} />

        {/* Two-light setup: warm key + cool fill */}
        <ambientLight intensity={0.15} color="#d4c5b0" />
        <pointLight position={[8, -6, 8]} intensity={0.7} color="#f5e6d3" distance={50} />
        <pointLight position={[-6, 8, 4]} intensity={0.35} color="#d4e5f7" distance={40} />

        {/* Dust particles */}
        <DustParticles count={isMobile ? 100 : 200} />

        {/* Cards */}
        <Suspense fallback={null}>
          {cardsWithPosition.map((card) => (
            <Card key={card.id} card={card} isMobile={isMobile} />
          ))}
        </Suspense>

        <OrbitControls enableZoom={false} enablePan={false} enableRotate={false} />
        <Preload all />
      </Canvas>
    </div>
  )
}
